import * as React from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { AppShell } from "@/components/app-shell";
import { ErrorBoundary } from "@/components/error-boundary";
import { PageLoading } from "@/components/states";
import { useAuth } from "@/hooks/use-auth";

const LoginPage = React.lazy(() => import("@/pages/login"));
const RegisterPage = React.lazy(() => import("@/pages/register"));
const VerifyEmailPage = React.lazy(() => import("@/pages/verify-email"));
const ForgotPasswordPage = React.lazy(() => import("@/pages/forgot-password"));
const DashboardPage = React.lazy(() => import("@/pages/dashboard"));
const SymptomsPage = React.lazy(() => import("@/pages/symptoms"));
const DocumentsPage = React.lazy(() => import("@/pages/documents"));
const ProfilePage = React.lazy(() => import("@/pages/profile"));
const ChatPage = React.lazy(() => import("@/pages/chat"));
const AnalysisPage = React.lazy(() => import("@/pages/analysis"));
const ProtocolPage = React.lazy(() => import("@/pages/protocol"));
const TrackPage = React.lazy(() => import("@/pages/track"));
const AccountPage = React.lazy(() => import("@/pages/account"));
const NotFoundPage = React.lazy(() => import("@/pages/not-found"));

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session, loading } = useAuth();
  if (loading) return <PageLoading />;
  if (!session) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

const RedirectIfAuthed: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { session, loading } = useAuth();
  if (loading) return <PageLoading />;
  if (session) return <Navigate to="/" replace />;
  return <>{children}</>;
};

export const App: React.FC = () => (
  <ErrorBoundary>
    <React.Suspense fallback={<PageLoading />}>
      <Routes>
        <Route
          path="/login"
          element={
            <RedirectIfAuthed>
              <LoginPage />
            </RedirectIfAuthed>
          }
        />
        <Route
          path="/register"
          element={
            <RedirectIfAuthed>
              <RegisterPage />
            </RedirectIfAuthed>
          }
        />
        <Route path="/verify" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="/symptoms" element={<SymptomsPage />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/analysis" element={<AnalysisPage />} />
          <Route path="/protocol" element={<ProtocolPage />} />
          <Route path="/track" element={<TrackPage />} />
          <Route path="/account" element={<AccountPage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </React.Suspense>
  </ErrorBoundary>
);

export default App;
